import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import getNextObjectKey from 'services/numbers/getNextObjectKey';
import InputLabel from 'components/layout/forms/InputLabel';
import IProductInstallationCategory from 'api/interfaces/products/IProductInstallationCategory';
import LinkButton from 'components/layout/buttons/LinkButton';
import translations from 'services/translations/translations';
import { moveItemWithinSubset } from 'services/arrays/moveItem';

import { INSTALLATION } from 'services/enums/offers/installationTypes';
import { PositionUnitPriceHeader } from '../PositionUnitPrice';
import { InstallationHistoryRows } from '../PositionHistory';

import getDefaultInstallation from './getDefaultInstallation';
import IInstallation from '../../IInstallation';
import Installation from './Installation';
import InstallationsSum from './InstallationsSum';
import RowOrder from '../RowOrder';
import { INSTALLATION as INSTALLATION_TYPE_INSTALLATION } from '../../../../services/installationTypes';

const Installations = ({
  locked,
  installations,
  quantity,
  onRemoveInstallation,
  reordering,
  setInstallations,
  targetMarginPercent,
}: {
  // Deleted rows are kept by the calculation window so they can be restored
  onRemoveInstallation: (installation: IInstallation, index: number) => void;
  locked: boolean;
  installations: IInstallation[];
  quantity: number;
  reordering: boolean;
  setInstallations: (installations: IInstallation[]) => void;
  // New rows follow the target margin % of the window
  targetMarginPercent: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.common, translations.offers]);

  const [openedInstallation, setOpenedInstallation] = useState<null | number>(
    null
  );

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  const addInstallation = () => {
    const key = getNextObjectKey(installations, 'key');
    setInstallations([
      ...installations,
      {
        ...getDefaultInstallation(key, INSTALLATION),
        followsTargetMargin: true,
        socialSecurityCostPercentage: '75',
      },
    ]);
  };

  const removeInstallation = (installation: IInstallation) => {
    onRemoveInstallation(
      installation,
      installations.findIndex(
        (existingInstallation) => existingInstallation.key === installation.key
      )
    );
    setInstallations(
      installations.filter(
        (existingInstallation) => existingInstallation.key !== installation.key
      )
    );
  };

  const setInstallation = (installation: IInstallation) => {
    setInstallations(
      installations.map((existingInstallation) =>
        existingInstallation.key === installation.key
          ? installation
          : existingInstallation
      )
    );
  };

  const isInstallation = (installation: IInstallation) =>
    installationCategories.some(
      (installationCategory: IProductInstallationCategory) =>
        installationCategory.type === INSTALLATION_TYPE_INSTALLATION &&
        installationCategory.category === installation.category
    );

  const installationsWithoutOtherExpenses =
    installations.filter(isInstallation);

  // Other expenses are in the same list; they keep their places
  const moveInstallation = (fromIndex: number, toIndex: number) =>
    setInstallations(
      moveItemWithinSubset(installations, isInstallation, fromIndex, toIndex)
    );

  const offerIsLocked = () => locked;

  const SMALL_COLUMN_CLASS = 'small-column';

  return (
    <>
      <h2>
        {t(`${translations.calculationWindow}:Installation and auxiliary work`)}
      </h2>
      <RowOrder
        count={installationsWithoutOtherExpenses.length}
        reordering={reordering}
      >
        <Row className="installation-row">
          <Col>
            <InputLabel>{t(`${translations.common}:Category`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>
              {t(`${translations.calculationWindow}:Installation`)}
            </InputLabel>
          </Col>
          <Col className="icon-column" />
          <Col>
            <InputLabel>{t(`${translations.common}:Quantity`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>
              {t(`${translations.calculationWindow}:Installation price`)}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {`${t(
                `${translations.calculationWindow}:Social security cost`
              )} %`}
            </InputLabel>
          </Col>
          <Col
            className={SMALL_COLUMN_CLASS}
            style={{ wordWrap: 'break-word' }}
          >
            <InputLabel>{t(`${translations.offers}:Expense`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>{t(`${translations.offers}:Selling price`)}</InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <PositionUnitPriceHeader />
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {`${t(`${translations.offers}:Contribution margin`)} €`}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {t(`${translations.calculationWindow}:Expense short`)}{' '}
              {t(`${translations.common}:total-short`)}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {t(`${translations.calculationWindow}:Selling short`)}{' '}
              {t(`${translations.common}:total-short`)}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {t(`${translations.offers}:Contribution margin`)}{' '}
              {t(`${translations.common}:total-short`)} €
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {`${t(`${translations.offers}:Contribution margin`)} %`}
            </InputLabel>
          </Col>
          <Col className="icon-column" />
        </Row>
      </RowOrder>
      {installationsWithoutOtherExpenses.map((installation, index) => (
        <React.Fragment key={`installation-${installation.key}`}>
          <RowOrder
            count={installationsWithoutOtherExpenses.length}
            index={index}
            move={moveInstallation}
            reordering={reordering}
          >
            <Installation
              installation={installation}
              locked={locked}
              openedInstallation={openedInstallation}
              quantity={quantity}
              removeInstallation={removeInstallation}
              setInstallation={setInstallation}
              setOpenedInstallation={setOpenedInstallation}
              targetMarginPercent={targetMarginPercent}
              type={INSTALLATION_TYPE_INSTALLATION}
            />
          </RowOrder>
          {reordering ? null : (
            <InstallationHistoryRows
              installation={installation}
              installations={installations}
              type={INSTALLATION_TYPE_INSTALLATION}
            />
          )}
        </React.Fragment>
      ))}
      {offerIsLocked() ? null : (
        <LinkButton onClick={addInstallation}>
          {`+ ${t(`${translations.common}:Add row`)}`}
        </LinkButton>
      )}
      <InstallationsSum
        quantity={quantity}
        installations={installationsWithoutOtherExpenses}
      />
    </>
  );
};

export default Installations;
